import React, { useState } from "react";
import {
    Container,
    Grid,
    TextField,
    Button,
    CircularProgress,
} from "@material-ui/core";
import Label from "../../../components/Label";
import { toast } from "react-toastify";
import EventApi from "../../../services/event";
import { config } from "../../../config";

const styled = {
    content: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px 10px",
    },
    form: { width: "100%", marginTop: "10px" },
    buttonRow: { display: "flex", gap: "10px", marginTop: "16px" },
};

const DEFAULT_EVENT_ID = 1;

const SponsorForm = ({ sponsor, close }) => {
    const isEdit = !!sponsor;
    const [values, setValues] = useState({
        name: sponsor?.name || "",
        description: sponsor?.description || "",
        total_slots: sponsor?.total_slots ?? "",
        event_id: sponsor?.event_id || DEFAULT_EVENT_ID,
    });
    const [loading, setLoading] = useState(false);

    function onChange(e) {
        const { name, value } = e.target;

        // Validação especial para total_slots na edição
        if (name === "total_slots" && isEdit && sponsor?.occupied_slots > 0) {
            const newValue = parseInt(value);
            if (!isNaN(newValue) && newValue < sponsor.occupied_slots) {
                toast.warn(`Total de vagas não pode ser menor que as vagas ocupadas (${sponsor.occupied_slots})`);
                return;
            }
            if (!isNaN(newValue) && newValue < sponsor.total_slots) {
                toast.warn(`Não é permitido reduzir o total de vagas. Valor atual: ${sponsor.total_slots}`);
                return;
            }
        }

        setValues((prev) => ({ ...prev, [name]: value }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        if (!values.name.trim()) {
            toast.warn("Nome do patrocinador é obrigatório");
            return;
        }
        if (values.total_slots === "" || isNaN(parseInt(values.total_slots)) || parseInt(values.total_slots) < 0) {
            toast.warn("Total de vagas deve ser um número maior ou igual a 0");
            return;
        }

        setLoading(true);
        const payload = {
            ...values,
            total_slots: parseInt(values.total_slots),
            event_id: parseInt(values.event_id),
        };

        try {
            let resp;
            if (isEdit) {
                resp = await EventApi.updateSponsor(sponsor.id, payload);
            } else {
                resp = await EventApi.createSponsor(payload);
            }

            // Sucesso: verifica se a resposta tem dados válidos
            if (resp?.data?.data || resp?.data?.id || resp?.data?.name) {
                toast.success(isEdit ? "Patrocinador atualizado com sucesso!" : "Patrocinador criado com sucesso!");
                close(true);
            }
            // Erro: trata a resposta de erro do backend
            else if (resp?.response?.data?.message) {
                const errorMsg = resp.response.data.message;
                toast.error(errorMsg);
            }
            // Erro: trata erro de validação do Joi
            else if (resp?.response?.data?.error) {
                toast.error(`Erro de validação: ${resp.response.data.error}`);
            }
            // Erro genérico
            else {
                toast.error(isEdit ? "Erro ao atualizar patrocinador" : "Erro ao criar patrocinador");
            }
        } catch (err) {
            console.error("Erro inesperado:", err);
            toast.error("Erro inesperado ao salvar patrocinador");
        } finally {
            setLoading(false);
        }
    }

    return (
        <Container component="main" maxWidth="sm">
            <div style={styled.content}>
                <Label size="medium" color="dark" align="center">
                    {isEdit ? "Editar Patrocinador" : "Novo Patrocinador"}
                </Label>
                <form style={styled.form} onSubmit={onSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Nome *"
                                name="name"
                                value={values.name}
                                onChange={onChange}
                                fullWidth
                                variant="outlined"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Descrição"
                                name="description"
                                value={values.description}
                                onChange={onChange}
                                fullWidth
                                variant="outlined"
                                size="small"
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Total de Vagas *"
                                name="total_slots"
                                type="number"
                                value={values.total_slots}
                                onChange={onChange}
                                fullWidth
                                variant="outlined"
                                size="small"
                                inputProps={{
                                    min: isEdit ? sponsor?.total_slots || 1 : 1,
                                    step: 1
                                }}
                                helperText={
                                    isEdit && sponsor?.occupied_slots >= 0
                                        ? `Vagas ocupadas: ${sponsor.occupied_slots}. Você só pode aumentar ou manter o total de vagas (mínimo: ${sponsor.total_slots}).`
                                        : "Mínimo de 1 vaga."
                                }
                            />
                        </Grid>
                    </Grid>
                    <div style={styled.buttonRow}>
                        <Button
                            variant="contained"
                            onClick={() => close(false)}
                            disabled={loading}
                            fullWidth
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={loading}
                            fullWidth
                        >
                            {loading ? <CircularProgress size={20} /> : isEdit ? "Salvar" : "Criar"}
                        </Button>
                    </div>
                </form>
            </div>
        </Container>
    );
};

export default SponsorForm;
